import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Heading,
  Loader,
  Flex,
  Label,
  Text,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  type TableColumn,
} from '@vibe/core';
import {
  getLeaveRequests,
  getLeaveTypes,
  deleteLeaveRequest,
  type LeaveRequest,
  type LeaveRequestStatus,
  type LeaveType,
} from '../../api/leave.api';
import { fetchMondayUsers, type MondayUser } from '../../api/monday.api';
import { formatDate } from '../../utils/dates';

// ─── Constants ────────────────────────────────────────────────────────────────

type StatusFilter = 'all' | LeaveRequestStatus;
const PAGE_SIZE = 20;

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'recalling', label: 'Recalling' },
  { key: 'recalled', label: 'Recalled' },
];

const STATUS_LABEL_COLOR: Record<
  LeaveRequestStatus,
  'EXPLOSIVE' | 'WORKING_ORANGE' | 'DONE_GREEN' | 'STUCK_RED' | 'DARK'
> = {
  pending: 'WORKING_ORANGE',
  approved: 'DONE_GREEN',
  rejected: 'STUCK_RED',
  recalling: 'EXPLOSIVE',
  recalled: 'DARK',
};

const COLUMNS: TableColumn[] = [
  { id: 'type', title: 'Type', width: { min: 100, max: '1fr' } },
  { id: 'person', title: 'Person', width: { min: 100, max: '1fr' } },
  { id: 'dates', title: 'Dates', width: { min: 140, max: '1.2fr' } },
  { id: 'days', title: 'Days', width: 60 },
  { id: 'note', title: 'Note', width: { min: 100, max: '1.2fr' } },
  { id: 'status', title: 'Status', width: 100 },
  { id: 'actions', title: 'Actions', width: 80 },
];

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  currentUserId: number | null;
}

const LeaveManageTab: React.FC<Props> = () => {
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [users, setUsers] = useState<MondayUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const fetchPage = useCallback(async (p: number, status: StatusFilter) => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = { page: p, pageSize: PAGE_SIZE };
      if (status !== 'all') params.status = status;
      const result = await getLeaveRequests(params as never);
      setRequests(result.data);
      setTotal(result.total);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  // Load leave types and users once
  useEffect(() => {
    Promise.all([getLeaveTypes(), fetchMondayUsers()])
      .then(([types, mondayUsers]) => {
        setLeaveTypes(types);
        setUsers(mondayUsers);
      })
      .catch(() => {});
  }, []);

  // Fetch requests when page or filter changes
  useEffect(() => {
    fetchPage(page, statusFilter);
  }, [page, statusFilter, fetchPage]);

  // Reset to page 1 when filter changes
  const handleFilterChange = useCallback((f: StatusFilter) => {
    setStatusFilter(f);
    setPage(1);
  }, []);

  const getTypeName = useCallback(
    (typeId: string) => leaveTypes.find((t) => t.id === typeId)?.name ?? typeId,
    [leaveTypes],
  );

  const getUserName = useCallback(
    (userId: string) => users.find((u) => String(u.id) === userId)?.name ?? `User ${userId}`,
    [users],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      if (!window.confirm('Are you sure you want to permanently delete this leave request?')) return;
      try {
        await deleteLeaveRequest(id);
        // Refetch current page
        fetchPage(page, statusFilter);
      } catch {
        // silently fail
      }
    },
    [page, statusFilter, fetchPage],
  );

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const showingFrom = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const showingTo = Math.min(page * PAGE_SIZE, total);

  const emptyState = (
    <div style={{ padding: 24, textAlign: 'center', color: 'var(--secondary-text-color)' }}>
      No leave requests found.
    </div>
  );

  const errorState = (
    <div style={{ padding: 24, textAlign: 'center', color: 'var(--negative-color)' }}>
      Failed to load leave requests.
    </div>
  );

  return (
    <div>
      <Flex
        align={Flex.align.CENTER}
        justify={Flex.justify.SPACE_BETWEEN}
        style={{ marginBottom: 16 }}
      >
        <Heading type={Heading.types.H2}>Manage Leave Requests</Heading>
      </Flex>

      <Text type={Text.types.TEXT2} style={{ color: 'var(--secondary-text-color)', marginBottom: 16 }}>
        As an admin, you can view and permanently delete any leave request regardless of status.
      </Text>

      {/* Status filter pills */}
      <Flex gap={Flex.gaps.XS} style={{ marginBottom: 16 }}>
        {STATUS_FILTERS.map((f) => (
          <Button
            key={f.key}
            kind={statusFilter === f.key ? Button.kinds.PRIMARY : Button.kinds.TERTIARY}
            size={Button.sizes.SMALL}
            onClick={() => handleFilterChange(f.key)}
          >
            {f.label}
          </Button>
        ))}
      </Flex>

      {loading ? (
        <Flex justify={Flex.justify.CENTER} style={{ padding: 40 }}>
          <Loader size={Loader.sizes.MEDIUM} />
        </Flex>
      ) : (
        <Table columns={COLUMNS} errorState={errorState} emptyState={emptyState}>
          <TableHeader>
            <TableHeaderCell title="Type" />
            <TableHeaderCell title="Person" />
            <TableHeaderCell title="Dates" />
            <TableHeaderCell title="Days" />
            <TableHeaderCell title="Note" />
            <TableHeaderCell title="Status" />
            <TableHeaderCell title="Actions" />
          </TableHeader>
          <TableBody>
            {requests.length === 0 ? (
              <TableRow>
                <TableCell>
                  <Text type={Text.types.TEXT2} style={{ color: 'var(--secondary-text-color)' }}>
                    No leave requests match this filter.
                  </Text>
                </TableCell>
              </TableRow>
            ) : (
              requests.map((req) => (
                <TableRow key={req.id}>
                  <TableCell>{getTypeName(req.leave_type_id)}</TableCell>
                  <TableCell>{getUserName(req.monday_user_id)}</TableCell>
                  <TableCell>
                    {formatDate(req.date_from)} – {formatDate(req.date_to)}
                  </TableCell>
                  <TableCell>
                    {req.total_days}
                    {req.capacity_type === 'half_day' ? ' (half)' : ''}
                  </TableCell>
                  <TableCell>
                    <Text
                      type={Text.types.TEXT2}
                      ellipsis
                      style={{ color: 'var(--primary-text-color)' }}
                    >
                      {req.note || '—'}
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Label
                      text={req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                      color={Label.colors[STATUS_LABEL_COLOR[req.status]]}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      kind={Button.kinds.TERTIARY}
                      size={Button.sizes.SMALL}
                      color={Button.colors.NEGATIVE}
                      onClick={() => handleDelete(req.id)}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      {/* Pagination */}
      {total > 0 && (
        <div className="rp-leave-request-list__pagination">
          <Text type={Text.types.TEXT2} color={Text.colors.SECONDARY}>
            {showingFrom}–{showingTo} of {total}
          </Text>
          <Flex gap={Flex.gaps.XS}>
            <Button
              size={Button.sizes.SMALL}
              kind={Button.kinds.TERTIARY}
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Prev
            </Button>
            <Button
              size={Button.sizes.SMALL}
              kind={Button.kinds.TERTIARY}
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </Flex>
        </div>
      )}
    </div>
  );
};

export default LeaveManageTab;
